/**
 * Collection PLP: saves visible breadcrumbs + PLP context to sessionStorage; if storage already has
 * a trail whose last crumb is this collection (e.g. from homepage promos), replace Liquid output first
 * — except when navigation came from another in-site page (menu, PLP→PLP), so a short stored trail
 * does not overwrite Liquid’s full Men > … > collection path; or when the last click was on a
 * link inside `#header-group` (`ownd_breadcrumb_nav_from_header`, set in `breadcrumb-home-click-trail.js`).
 * PDP: optional **Home > Product** when `ownd_breadcrumb_home_pdp` matches (main-content product
 * click, see `breadcrumb-home-click-trail.js`); else restores [trail] + product when URL/referrer
 * validates (same header skip).
 */
(function () {
  const STORAGE_KEY = 'ownd_breadcrumb_trail';
  const HOME_PDP_KEY = 'ownd_breadcrumb_home_pdp';
  const FROM_HEADER_NAV_KEY = 'ownd_breadcrumb_nav_from_header';
  const TRAIL_SELECTOR = 'ol[data-ownd-breadcrumb-trail]';

  /**
   * Theme may render two custom-breadcrumb sections (desktop-only + mobile-only) with the same
   * logical trail; only one is visible. getElementById would always hit the first (often hidden).
   */
  function isRenderedVisible(el) {
    let n = el;
    while (n && n !== document.documentElement) {
      const st = window.getComputedStyle(n);
      if (st.display === 'none' || st.visibility === 'hidden') return false;
      n = n.parentElement;
    }
    return true;
  }

  function getTrailOl() {
    const all = Array.from(document.querySelectorAll(TRAIL_SELECTOR));
    if (!all.length) return null;
    const visible = all.find((ol) => isRenderedVisible(ol));
    return visible || all[all.length - 1];
  }

  function isCollectionPlpPath() {
    const p = window.location.pathname;
    return p.includes('/collections/') && !p.includes('/products/');
  }

  function isProductPath() {
    const p = window.location.pathname;
    if (/\/products\/[^/]+\/?$/i.test(p)) return true;
    if (/\/collections\/[^/]+\/products\/[^/]+\/?$/i.test(p)) return true;
    return false;
  }

  function collectionHandleFromProductUrl() {
    const p = window.location.pathname;
    const m = p.match(/\/collections\/([^/]+)\/products\//i);
    return m && m[1] ? m[1].split('?')[0] : '';
  }

  function readPlpPageContext() {
    const el = document.getElementById('ownd-plp-collection-context');
    if (!el?.textContent) return null;
    try {
      const o = JSON.parse(el.textContent.trim());
      if (!o || !o.handle) return null;
      return {
        handle: String(o.handle),
        title: String(o.title || o.handle),
        url: String(o.url || ''),
      };
    } catch {
      return null;
    }
  }

  function collectionHandleFromShopifyPath(pathname) {
    const marker = '/collections/';
    const idx = pathname.indexOf(marker);
    if (idx === -1) return '';
    const rest = pathname.slice(idx + marker.length);
    return rest.split('/')[0].split('?')[0] || '';
  }

  function collectionHandleFromHref(href) {
    try {
      const u = new URL(href, window.location.origin);
      return collectionHandleFromShopifyPath(u.pathname);
    } catch {
      return '';
    }
  }

  function saveTrailFromDom() {
    const ol = getTrailOl();
    if (!ol) return;

    const trail = [];
    ol.querySelectorAll(':scope > li').forEach((li) => {
      const a = li.querySelector(':scope > a[href]');
      if (!a) return;
      const title = (a.textContent || '').trim();
      const url = a.getAttribute('href') || a.href;
      if (!title || !url) return;
      trail.push({ title, url });
    });

    if (!trail.length) return;

    const ctx = readPlpPageContext();
    let out = trail;
    if (ctx?.handle && ctx.url) {
      const last = trail[trail.length - 1];
      const lastHandle = last ? collectionHandleFromHref(last.url) : '';
      if (lastHandle !== ctx.handle) {
        out = trail.concat([{ title: ctx.title, url: ctx.url }]);
      }
    }

    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(out));
    } catch {}
  }

  function readTrail() {
    try {
      const raw = sessionStorage.getItem(STORAGE_KEY);
      if (!raw) return null;
      const data = JSON.parse(raw);
      return Array.isArray(data) && data.length ? data : null;
    } catch {
      return null;
    }
  }

  /**
   * When the shopper reached this PLP via in-site navigation (e.g. header menu Men → … → Polos),
   * the referrer is another storefront path — not home. Do not replace Liquid’s deeper trail with
   * a shorter `ownd_breadcrumb_trail` from an earlier homepage/promo click to the same collection.
   */
  function isSameOriginReferrerFromNonHomePage() {
    if (!document.referrer) return false;
    try {
      const ref = new URL(document.referrer, window.location.origin);
      if (ref.origin !== window.location.origin) return false;
      let p = ref.pathname.split('?')[0];
      if (p.length > 1) p = p.replace(/\/+$/, '');
      if (!p || p === '/' || p === '/index') return false;
      return true;
    } catch {
      return false;
    }
  }

  /** Next PLP/PDP should keep Liquid crumbs when the navigation link lived under `#header-group`. */
  function consumeSkipStoredTrailAfterHeaderNavigation() {
    try {
      if (sessionStorage.getItem(FROM_HEADER_NAV_KEY) !== '1') return false;
      sessionStorage.removeItem(FROM_HEADER_NAV_KEY);
      return true;
    } catch {
      return false;
    }
  }

  function normalizePathForCompare(pathname) {
    if (!pathname) return '';
    let p = pathname.split('?')[0];
    if (p.length > 1) p = p.replace(/\/+$/, '');
    return p.toLowerCase();
  }

  function getRootHrefForTrail() {
    const ol = getTrailOl();
    const section = ol?.closest('[data-root-url]');
    if (section instanceof HTMLElement) {
      const raw = section.getAttribute('data-root-url');
      if (raw) return raw;
    }
    const firstA = ol?.querySelector(':scope > li:first-child a[href]');
    return firstA?.getAttribute('href') || '/';
  }

  /**
   * Main-content product click: show Home → current product only when storage matches this URL.
   * @returns {boolean} true if the visible trail was replaced
   */
  function applyHomeDirectPdpBreadcrumb() {
    let payload = null;
    try {
      const raw = sessionStorage.getItem(HOME_PDP_KEY);
      if (!raw) return false;
      payload = JSON.parse(raw);
    } catch {
      return false;
    }
    if (!payload || typeof payload.path !== 'string') return false;

    const here = normalizePathForCompare(window.location.pathname);
    const there = normalizePathForCompare(payload.path);
    if (!here || !there || here !== there) return false;

    const nav = getTrailOl();
    if (!nav) return false;

    const productLi = nav.querySelector('a[aria-current="page"]')?.closest('li');
    if (!productLi) return false;

    const ol = document.createElement('ol');
    ol.id = nav.id;
    ol.className = nav.className;
    ol.setAttribute('data-ownd-breadcrumb-trail', '');

    const rootHref = getRootHrefForTrail();
    const homeLi = document.createElement('li');
    const homeA = document.createElement('a');
    homeA.href = rootHref;
    homeA.textContent = 'Home';
    homeLi.appendChild(homeA);
    ol.appendChild(homeLi);

    const productClone = productLi.cloneNode(true);
    productClone.querySelectorAll('[aria-current]').forEach((el) => el.removeAttribute('aria-current'));
    const pA = productClone.querySelector('a[href]');
    if (pA) pA.setAttribute('aria-current', 'page');
    ol.appendChild(productClone);

    nav.replaceWith(ol);

    try {
      sessionStorage.removeItem(HOME_PDP_KEY);
    } catch {}

    return true;
  }

  /**
   * Homepage (and other promos) can set `ownd_breadcrumb_trail` before navigating to a collection.
   * Liquid still renders the menu-based path. When the stored trail ends on this collection handle,
   * replace the visible <ol> with the stored trail so it matches what the shopper chose.
   * The last crumb uses `collection.title` from the page (not the stored handle/slug label).
   */
  function applyTrailOnCollectionIfStored() {
    const ctx = readPlpPageContext();
    if (!ctx?.handle) return;

    const trail = readTrail();
    if (!trail || trail.length < 2) return;

    const last = trail[trail.length - 1];
    const trailTailHandle = collectionHandleFromHref(last.url);
    if (!trailTailHandle || trailTailHandle !== ctx.handle) return;

    const nav = getTrailOl();
    if (!nav) return;

    if (consumeSkipStoredTrailAfterHeaderNavigation()) {
      return;
    }

    if (isSameOriginReferrerFromNonHomePage()) {
      return;
    }

    const ol = document.createElement('ol');
    ol.id = nav.id;
    ol.className = nav.className;
    ol.setAttribute('data-ownd-breadcrumb-trail', '');

    trail.forEach((seg, i) => {
      const li = document.createElement('li');
      const a = document.createElement('a');
      a.href = seg.url;
      const isLast = i === trail.length - 1;
      const segHandle = collectionHandleFromHref(seg.url);
      const label =
        isLast && segHandle === ctx.handle
          ? ctx.title || seg.title
          : seg.title;
      a.textContent = label;
      if (isLast) {
        a.setAttribute('aria-current', 'page');
      }
      li.appendChild(a);
      ol.appendChild(li);
    });

    nav.replaceWith(ol);
  }

  function readProductHandles() {
    const el = document.getElementById('ownd-product-collection-handles');
    if (!el?.textContent) return [];
    try {
      const arr = JSON.parse(el.textContent.trim());
      if (!Array.isArray(arr)) return [];
      return [...new Set(arr.filter(Boolean))];
    } catch {
      return [];
    }
  }

  function plpHandleForValidation(trailLastHandle) {
    const fromUrl = collectionHandleFromProductUrl();
    if (fromUrl && fromUrl === trailLastHandle) return fromUrl;

    let refPath = '';
    try {
      refPath = document.referrer
        ? new URL(document.referrer, window.location.origin).pathname
        : '';
    } catch {
      return '';
    }
    const fromRef = collectionHandleFromShopifyPath(refPath);
    if (fromRef && fromRef === trailLastHandle) return fromRef;

    // Canonical `/products/...` on mobile often has no Referer (in-app browsers, stripped referrer).
    // `handles.includes(trailHandle)` already limits the trail to this product's collections.
    if (!fromUrl && !document.referrer) {
      return trailLastHandle;
    }

    return '';
  }

  function applyTrailOnPdp() {
    const trail = readTrail();
    if (!trail?.length) return;

    const last = trail[trail.length - 1];
    const trailHandle = collectionHandleFromHref(last.url);
    if (!trailHandle) return;

    const handles = readProductHandles();
    if (!handles.length || !handles.includes(trailHandle)) return;

    if (!plpHandleForValidation(trailHandle)) return;

    const nav = getTrailOl();
    if (!nav) return;

    if (consumeSkipStoredTrailAfterHeaderNavigation()) {
      return;
    }

    const productLi = nav.querySelector('a[aria-current="page"]')?.closest('li');
    if (!productLi) return;

    const ol = document.createElement('ol');
    ol.id = nav.id;
    ol.className = nav.className;
    ol.setAttribute('data-ownd-breadcrumb-trail', '');

    trail.forEach((seg) => {
      const li = document.createElement('li');
      const a = document.createElement('a');
      a.href = seg.url;
      a.textContent = seg.title;
      li.appendChild(a);
      ol.appendChild(li);
    });

    const productClone = productLi.cloneNode(true);
    productClone.querySelectorAll('[aria-current]').forEach((el) => el.removeAttribute('aria-current'));
    const pA = productClone.querySelector('a[href]');
    if (pA) pA.setAttribute('aria-current', 'page');
    ol.appendChild(productClone);

    nav.replaceWith(ol);
  }

  function run() {
    if (isCollectionPlpPath()) {
      applyTrailOnCollectionIfStored();
      saveTrailFromDom();
    } else if (isProductPath()) {
      if (consumeSkipStoredTrailAfterHeaderNavigation()) {
        return;
      }
      if (!applyHomeDirectPdpBreadcrumb()) {
        applyTrailOnPdp();
      }
    }
  }

  function runWhenReady() {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', run, { once: true });
    } else {
      run();
    }
    requestAnimationFrame(function () {
      if (isCollectionPlpPath()) saveTrailFromDom();
    });
  }

  runWhenReady();
})();
